"use client";

import useMediaQuery, { MD_DOWN_MEDIA_QUERY } from "@/app/hooks/useMediaQuery";
import { useAppSelector } from "@/store/hooks";
import {
  fetchProductPageBanner,
  getBannerLink,
  logBannerWinner,
  PRODUCT_PAGE_BANNER_SLOT_ID,
} from "@/utils/topsortUtil";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useInView } from "react-intersection-observer";
import styles from "./productPageBannerAd.module.scss";

const AUCTION_TIMEOUT_MS = 2000;

/**
 * Topsort banner for campaigns that target any of the product's categories. The auction runs once
 * the slot scrolls into view, and the slot stays empty unless it has a winner.
 */
export default function ProductPageBannerAd({ mpId, categoryIds, className }: ProductPageBannerAdProps) {
  const device = useMediaQuery(MD_DOWN_MEDIA_QUERY) ? "mobile" : "desktop";

  if (categoryIds.length === 0) {
    return null;
  }
  // Each product, creative size and category list gets its own auction, starting from an empty slot.
  return (
    <ErrorBoundary fallback={null}>
      <BannerSlot
        key={`${mpId}-${device}-${categoryIds.join(",")}`}
        device={device}
        categoryIds={categoryIds}
        className={className}
      />
    </ErrorBoundary>
  );
}

function BannerSlot({ device, categoryIds, className }: BannerSlotProps) {
  const apiKey = useAppSelector((state) => state.config.topsortJsApiKey);
  // Stays true once seen, so scrolling past doesn't cancel an auction that's under way.
  const { ref, inView } = useInView({ triggerOnce: true });
  const [winner, setWinner] = useState<Winner | null>(null);
  const categoryKey = categoryIds.join(",");

  useEffect(() => {
    if (!inView || !apiKey) {
      return;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), AUCTION_TIMEOUT_MS);
    fetchProductPageBanner(categoryKey.split(","), apiKey, device, controller.signal)
      .finally(() => window.clearTimeout(timeout))
      .then(async (result) => {
        const imageUrl = result?.asset?.[0]?.url;
        if (result && imageUrl) {
          // Shows the banner fully drawn, and never as a broken image.
          await loadImage(imageUrl);
          if (!controller.signal.aborted) {
            setWinner(result);
          }
        }
      })
      .catch(() => {});

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [inView, apiKey, device, categoryKey]);

  return (
    <div ref={ref} className={className}>
      {winner && <SponsoredBanner key={winner.resolvedBidId} winner={winner} onImageError={() => setWinner(null)} />}
    </div>
  );
}

function SponsoredBanner({ winner, onImageError }: SponsoredBannerProps) {
  // Logs the win when Topsort counts the impression: once half of the link is on screen.
  const { ref } = useInView({
    threshold: 0.5,
    triggerOnce: true,
    onChange: (inView) => {
      if (inView) {
        logBannerWinner(PRODUCT_PAGE_BANNER_SLOT_ID, winner);
      }
    },
  });

  return (
    <div className={styles.ProductPageBannerAd}>
      {/* Topsort's analytics script reports the impression and click from these attributes. */}
      <a
        ref={ref}
        href={getBannerLink(winner)}
        className={styles.link}
        data-ts-resolved-bid={winner.resolvedBidId}
        data-ts-product={winner.type === "product" ? winner.id : undefined}
      >
        <img className={styles.image} src={winner.asset[0].url} alt="Sponsored ad" onError={onImageError} />
      </a>
      <p className={styles.label}>Sponsored</p>
    </div>
  );
}

function loadImage(url: string): Promise<void> {
  const image = new Image();
  image.src = url;
  return image.decode();
}

type BannerDevice = "desktop" | "mobile";

interface ProductPageBannerAdProps {
  mpId: number;
  /** Every category the product is in, including parent categories. */
  categoryIds: string[];
  /** Applied to the slot, which has no children until an auction is won. */
  className?: string;
}

interface BannerSlotProps {
  device: BannerDevice;
  categoryIds: string[];
  className?: string;
}

interface SponsoredBannerProps {
  winner: Winner;
  /** The preload doesn't guarantee this image loads too. */
  onImageError: () => void;
}
